'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import PointLoungeEarnListItem from '@/app/(football)/[team]/_components/common/PointLoungeEarnListItem';
import { useCurrentPath } from '@/hooks/useCurrentPath';
import { trackEvent } from '@/libs/analytics/events';
import { useAppDispatch } from '@/store/client/config';
import { modalActions } from '@/store/client/slices/modalSlice';
import { USE_FOOTBALL_QUERY_KEY, useSavePointMutation } from '@/store/server/common/football/football.query';
import { POINT_METHOD_CODES } from '@/store/server/common/point/point.dto';
import { USE_POINT_QUERY_KEY } from '@/store/server/common/point/point.query';
import { checkLogin } from '@/utils/auth';

import EarnSuccessModal from './Modal/EarnSuccessModal';
import OfflineEventConfirmModal from './Modal/OfflineEventConfirmModal';

interface EarnListItemProps {
  methodIdx: number;
  methodCode: string;
  title: string;
  image: string;
  link?: string;
  point: number;
  offlineYn: 'Y' | 'N';
  startDttm: number;
  endDttm: number;
  hasCompleted?: boolean;
}

export default function EarnListItem({
  methodIdx,
  methodCode,
  title,
  image,
  link,
  point,
  offlineYn,
  startDttm,
  endDttm,
  hasCompleted = false,
}: EarnListItemProps) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const currentPath = useCurrentPath();
  const dispatch = useAppDispatch();

  const { mutate: savePoint, isPending: isSaving } = useSavePointMutation({
    onSuccess: () => {
      trackEvent({
        name: 'earning_point',
        params: {
          point: point,
          value: point,
          action_type: methodCode,
        },
      });
      dispatch(
        modalActions.openComponentModal({
          component: <EarnSuccessModal point={point} />,
        }),
      );
      queryClient.invalidateQueries({ queryKey: [USE_POINT_QUERY_KEY.getMyPoint] });
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getPointSave] });
    },
  });

  const handleClick = () => {
    if (!checkLogin()) {
      dispatch(
        modalActions.openConfirmModal({
          icon: 'danger',
          title: ['포인트 라운지를 이용하려면', '로그인해주세요.'],
          confirmText: ['로그인하기'],
          closeText: ['확인'],
          onConfirm: () => {
            router.push(`/account/login?retUrl=${currentPath}`);
          },
        }),
      );
      return;
    }

    if (!hasCompleted) {
      if (methodCode === POINT_METHOD_CODES.CLICK.code && link) {
        window.open(link, '_blank');
      }
      savePoint({ methodIdx, methodCode });
      return;
    }

    if (hasCompleted && methodCode === POINT_METHOD_CODES.OFFLINE.code && offlineYn === 'N') {
      dispatch(
        modalActions.openComponentModal({
          component: <OfflineEventConfirmModal methodIdx={methodIdx} methodCode={methodCode} />,
        }),
      );
      return;
    }
  };

  const isDisabled =
    (methodCode !== POINT_METHOD_CODES.OFFLINE.code && hasCompleted) ||
    (methodCode === POINT_METHOD_CODES.OFFLINE.code && hasCompleted && offlineYn === 'Y');

  const buttonLabel = (() => {
    if (methodCode === POINT_METHOD_CODES.OFFLINE.code && offlineYn === 'N' && !hasCompleted) {
      return '참여하기';
    }
    if (methodCode === POINT_METHOD_CODES.OFFLINE.code && offlineYn === 'N' && hasCompleted) {
      return '수령 확인하기';
    }
    if (methodCode === POINT_METHOD_CODES.OFFLINE.code && offlineYn === 'Y' && hasCompleted) {
      return '수령완료';
    }
    return `${point.toLocaleString('ko-KR')}P 받기`;
  })();

  return (
    <PointLoungeEarnListItem
      title={title}
      image={image}
      point={point}
      startDttm={startDttm}
      endDttm={endDttm}
      isCompleted={isDisabled}
      onClick={handleClick}
      buttonLabel={buttonLabel}
      disabled={isDisabled}
      isLoading={isSaving}
    />
  );
}
